import { defineStore } from "pinia";
import piniaPersistConfig from "@/stores/helper/persist";
import { UserVo, User, Supplier } from "@supplier/entity";
import { loginService } from "@/api/services/loginService";
import { ThemeOptions } from "@/views/layouts/type";
import { SYSTEM_TYPE, YES_OR_NO } from "@supplier/meta";

export interface UserStoreState {
    userInfo: UserVo;
    token: string;
    mobile: string;
    theme: ThemeOptions;
    // 完成企业认证
    enterpriseAuth: boolean;
}
export interface UserStoreGetters {
    customerId: number | undefined;
    isSAAS: boolean;
    isOrganizationCertified: boolean;
}
export interface UserStoreActions {
    setToken: (token: string) => void;
    setUserInfo: (userInfo: UserVo) => Promise<void>;
    setTheme: (theme: ThemeOptions) => void;
    refreshUserInfo: (reload?: boolean) => Promise<void>;
    clear: () => void;
    setMobile: (mobile: string) => void;
    setEnterpriseAuth: (enterpriseAuth: boolean) => void;
}

export type UserStore = UserStoreState & UserStoreGetters & UserStoreActions;

export const useUserStore = defineStore({
    id: "zhongsheng-user",
    state: (): UserStoreState => ({
        theme: {} as ThemeOptions,
        token: "",
        userInfo: {
            roles: [],
            user: {} as unknown as User,
            permissions: [],
            permissionsObj: {},
            systemType: 0,
            // 供应链信息
            supplier: {} as unknown as Supplier
        },
        mobile: "",
        enterpriseAuth: true
    }),
    getters: {
        customerId({ userInfo }) {
            return userInfo.user?.id;
        },
        // 是否是SAAS供应链, 区分是SAAS,独立部署
        isSAAS: ({ userInfo }) => {
            return userInfo.systemType === SYSTEM_TYPE.SAAS.code;
        },
        // SAAS供应链是否完成组织机构认证(独立部署不需要认证)
        isOrganizationCertified({ userInfo }) {
            const isSAAS = userInfo.systemType === SYSTEM_TYPE.SAAS.code;
            return isSAAS && userInfo?.supplier?.applyState === YES_OR_NO.YES.code;
        }
    },
    actions: {
        setToken(token: string) {
            this.token = token;
        },
        async setUserInfo(userInfo: UserVo) {
            this.userInfo = userInfo;
            // 权限列表对象
            const permissionObj: { [key: string]: boolean } = {};
            userInfo.permissions.forEach(permission => {
                permissionObj[permission] = true;
            });
            this.userInfo.permissionsObj = permissionObj;
        },
        setTheme(theme: ThemeOptions) {
            this.theme = theme;
        },
        async refreshUserInfo(reload: boolean = true) {
            const data: UserVo = await loginService.getUserInfo();
            await this.setUserInfo(data);
            // 企业认证状态
            this.setEnterpriseAuth(data.supplier.applyState);
            if (reload) location.reload();
        },
        clear() {
            this.token = "";
            this.userInfo = {
                roles: [],
                user: {} as unknown as User,
                permissions: [],
                permissionsObj: {},
                supplier: {},
                systemType: 0
            };
        },
        setMobile(mobile: string) {
            this.mobile = mobile;
        },
        setEnterpriseAuth(enterpriseAuth: boolean) {
            // 独立部署忽略认证校验
            this.enterpriseAuth = this.isSAAS ? enterpriseAuth : true;
        }
    },
    persist: piniaPersistConfig("zhongsheng-user")
});
